import { useSyncExternalStore } from 'react';

// Lightweight localStorage-backed store for product comparison + recently viewed.
// Uses cached snapshots + useSyncExternalStore so every subscribed component
// re-renders the instant the store changes, with no infinite loops.

const RV_KEY = 'mr_recently_viewed';
const CMP_KEY = 'mr_compare';
const MAX_RV = 12;
const MAX_CMP = 4;

const listeners = new Set();
let rvCache = null;
let cmpCache = null;

function readRaw(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

function getRV() {
  if (rvCache === null) rvCache = readRaw(RV_KEY, []);
  return rvCache;
}
function getCmp() {
  if (cmpCache === null) cmpCache = readRaw(CMP_KEY, []);
  return cmpCache;
}

function emit() {
  listeners.forEach((l) => l());
}
function persistRV() {
  localStorage.setItem(RV_KEY, JSON.stringify(rvCache));
  emit();
}
function persistCmp() {
  localStorage.setItem(CMP_KEY, JSON.stringify(cmpCache));
  emit();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Derive a product brand: explicit brand field wins, otherwise the leading
// token of the product name (works well for this catalog where the brand
// leads the name, e.g. "Simucube 3 Ultimate").
export function deriveBrand(product) {
  if (!product) return '';
  if (product.brand && String(product.brand).trim()) return String(product.brand).trim();
  const name = String(product.name || '').trim();
  if (!name) return '';
  return name.split(/\s+/)[0];
}

// ---- Recently viewed ----
export function addRecentlyViewed(id) {
  if (!id) return;
  const cur = getRV().filter((x) => x !== id);
  cur.unshift(id);
  rvCache = cur.slice(0, MAX_RV);
  persistRV();
}
export function clearRecentlyViewed() {
  rvCache = [];
  persistRV();
}

// ---- Compare ----
export const MAX_COMPARE = MAX_CMP;
export function getCompareIds() {
  return getCmp();
}
export function isInCompare(id) {
  return getCmp().includes(id);
}
export function toggleCompare(id) {
  const cur = getCmp();
  const i = cur.indexOf(id);
  if (i >= 0) {
    cmpCache = cur.filter((x) => x !== id);
  } else if (cur.length < MAX_CMP) {
    cmpCache = [...cur, id];
  } else {
    return false; // compare tray is full
  }
  persistCmp();
  return true;
}
export function removeCompare(id) {
  cmpCache = getCmp().filter((x) => x !== id);
  persistCmp();
}
export function clearCompare() {
  cmpCache = [];
  persistCmp();
}

// ---- React hooks (stable cached snapshots) ----
export function useCompare() {
  return useSyncExternalStore(subscribe, getCmp, getCmp);
}
export function useRecentlyViewed() {
  return useSyncExternalStore(subscribe, getRV, getRV);
}